import React from "react";
import { RecommendationProjectTypeEnum } from "../../../types";
import { CurrentProjectGrid } from "../components/CurrentProjectGrid";
import { ProjectGrid } from "../components/ProjectGrid";
import CardRecommendationInfo from "../../../components/Recommendations/CardRecommendationInfo";
import CardForestRecommendationInfo from "../../../components/Recommendations/CardForestRecommendationInfo";
import { SkeletonLoader } from "../components/SkeletonLoader";
import { getProjectGridLoadingState, getEmptyRecommendationMessage } from "../utils/projectUtils";
import { useHomePageContext } from "../context/HomePageContext";

/**
 * Custom hook for project grid rendering logic
 * Following Clean Code principle: Extract complex rendering logic into focused hook
 */
export const useProjectGrid = () => {
  const { projectData, uiState, computed } = useHomePageContext();

  /**
   * Renders the appropriate project grid based on selected project type
   * Following Clean Code principle: Self-documenting function name
   */
  const renderProjectGrid = (): React.ReactNode => {
    const isLoading = getProjectGridLoadingState(
      uiState.selectedProject,
      projectData.carbonProjects.isLoading,
      computed.isLoadingRecommended,
    );

    if (isLoading) {
      return <SkeletonLoader />;
    }

    return renderProjectsByType();
  };

  /**
   * Renders projects based on the selected project type
   * Following Clean Code principle: Break down complex switch into focused function
   */
  const renderProjectsByType = (): React.ReactNode => {
    switch (uiState.selectedProject) {
      case "current":
        return <CurrentProjectGrid carbonProjects={projectData.carbonProjects.items} />;

      case "recommendation":
        return renderRecommendationProjects();

      case "saved":
        return <ProjectGrid projects={projectData.favoriteProjects.items} />;

      default:
        return null;
    }
  };

  /**
   * Renders recommendation projects with empty state handling
   * Following Clean Code principle: Single responsibility for recommendation rendering
   */
  const renderRecommendationProjects = (): React.ReactNode => {
    if (!computed.currentRecommendedProjects || computed.currentRecommendedProjects.length === 0) {
      return renderEmptyRecommendationState();
    }

    return uiState.selectedRecommendation === RecommendationProjectTypeEnum.Forests
      ? renderForestRecommendations()
      : renderMangroveRecommendations();
  };

  /**
   * Renders empty state for recommendations
   * Following Clean Code principle: Extract UI state rendering
   */
  const renderEmptyRecommendationState = (): React.ReactNode => {
    const { mainMessage, subMessage } = getEmptyRecommendationMessage(
      uiState.selectedRecommendation,
    );

    return (
      <div className="flex w-full flex-col items-center justify-center p-8 text-center">
        <div className="text-gray-500">{mainMessage}</div>
        <div className="mt-2 text-sm text-gray-400">{subMessage}</div>
      </div>
    );
  };

  /**
   * Renders forest recommendation cards
   * Following Clean Code principle: Focused function for specific rendering
   */
  const renderForestRecommendations = (): React.ReactNode => {
    return computed.currentRecommendedProjects.map((item) => (
      <CardForestRecommendationInfo
        key={`forest-recommendation-${item.id}`}
        id={item.id}
        country_code={item.country_code}
        country={item.country}
        adm1={item.adm1}
        adm2={item.adm2}
        area={item.area || 0}
      />
    ));
  };

  /**
   * Renders mangrove recommendation cards
   * Following Clean Code principle: Focused function for specific rendering
   */
  const renderMangroveRecommendations = (): React.ReactNode => {
    return computed.currentRecommendedProjects.map((item) => (
      <CardRecommendationInfo
        key={`mangrove-recommendation-${item.id}`}
        name={item.name || ""}
        subtype={item.subtype}
        status={item.status}
        potential_mangrove={item.potential_mangrove}
        project_type={item.project_type}
        mangrove_coverage={item.mangrove_coverage}
        mangrove_degradation={item.mangrove_degradation}
        name_one={item.name_one}
        country={item.country}
        id={item.id}
      />
    ));
  };

  return {
    renderProjectGrid,
  };
};
